import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useParams, useNavigate } from 'react-router-dom';
import { BlogPost, User } from '../types';
import BlogCard from './BlogCard';
import { fetchUser, fetchUserBlogs, getBio } from '../services/api';

const ProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'posts' | 'about'>('posts');
  const [user, setUser] = useState<User | null>(null);
  const [userPosts, setUserPosts] = useState<BlogPost[]>([]);
  const [userBio, setUserBio] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [bioLoading, setBioLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user data from API
  useEffect(() => {
    const loadUserData = async () => {
      if (!username) {
        setError('No username provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch user data (using username as user ID since API uses IDs)
        const user = await fetchUser(username);
        setUser(user);

        // Fetch user's blogs
        const blogs = await fetchUserBlogs(username);
        setUserPosts(blogs);
        setError(null);
      } catch (err) {
        setError('User not found');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [username]);

  // Load user's Bio Blog when user is loaded
  useEffect(() => {
    const loadUserBio = async () => {
      if (!user?.bio_id) return;

      try {
        setBioLoading(true);
        const bioBlog = await getBio(user.username);
        if (bioBlog) {
          setUserBio(bioBlog);
        } else {
          // If no bio blog exists, set default message
          setUserBio({
            id: user.bio_id,
            content: `## About ${user.name}\n\n${user.name} hasn't written their bio yet.`,
            author_username: user.username,
            posted_at: '',
          });
        }
      } catch (error) {
        setUserBio({
          id: user.bio_id,
          content: `## About ${user.name}\n\n${user.name} hasn't written their bio yet.`,
          author_username: user.username,
          posted_at: '',
        });
      } finally {
        setBioLoading(false);
      }
    };

    loadUserBio();
  }, [user]);

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-content">
          <div className="loading-state">Loading user profile...</div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="profile-page">
        <div className="profile-content">
          <div className="user-not-found">
            <h1>User not found</h1>
            <p>The user @{username || 'unknown'} doesn't exist.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page minimal-profile">
      <div className="profile-header minimal-header">
        <h1>{user.name}</h1>
        <div className="username">@{user.username}</div>
        <div className="username">{user.quote}</div>
      </div>
      <div className="profile-tabs">
        <div className="tabs-container">
          <button
            className={`tab-button ${activeTab === 'posts' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('posts')}
          >Posts</button>
          <button
            className={`tab-button ${activeTab === 'about' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('about')}
          >About</button>
        </div>
        <div className="tabs-underline"></div>
      </div>
      <div className="profile-content minimal-content">
        {activeTab === 'posts' ? (
          <main className="posts-section minimal-posts">
            {userPosts.length === 0 ? (
              <div className="no-posts">
                <p>{user.name} hasn't published any posts yet.</p>
              </div>
            ) : (
              <>
                {/* Big first post (featured) */}
                <div className="featured-post-container">
                  <BlogCard
                    key={userPosts[0].id}
                    post={userPosts[0]}
                    onClick={() => navigate(`/blog/${userPosts[0].slug || userPosts[0].id}`)}
                  />
                </div>
                {/* Remaining posts */}
                <div className="posts-grid">
                  {userPosts.slice(1).map(post => (
                    <BlogCard
                      key={post.id}
                      post={post}
                      onClick={() => navigate(`/blog/${post.slug || post.id}`)}
                    />
                  ))}
                </div>
              </>
            )}
          </main>
        ) : (
          <section className="about-section minimal-about">
            {bioLoading ? (
              <div className="loading-state">Loading bio...</div>
            ) : (
              <div className="article-body">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                >
                  {userBio?.content || `## About ${user.name}\n\n${user.name} hasn't written their bio yet.`}
                </ReactMarkdown>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;